import React, { useMemo, useEffect } from "react";
import { useDispatch } from "react-redux";
import { Form, InputGroup, Button } from "react-bootstrap";
import { Dropdown, Checkbox } from "semantic-ui-react";
import { FaSearch, FaTimes } from "react-icons/fa";
import { setClusterData } from "../redux/windowData";

import "./WindowFilters.css";
import {
  clusterWindowsByPolygonType,
  calculateArea,
  kMeansClustering,
} from "./utility/dataManipulation";

const WindowFilters = ({ allWindows, floors, filters, onFiltersChange }) => {
  const dispatch = useDispatch();

  const floorOptions = useMemo(() => {
    const uniqueFloors = [...new Set(allWindows.map((w) => w.floorIndex))];
    return uniqueFloors.map((floorIndex) => ({
      key: floorIndex,
      value: floorIndex,
      text: floors[floorIndex] || `Floor ${floorIndex + 1}`,
    }));
  }, [allWindows, floors]);

  const shapeSizeOptions = useMemo(() => {
    if (allWindows.length === 0) {
      return [];
    }

    const { clusters } = clusterWindowsByPolygonType(allWindows, 20);

    return clusters
      .map((cluster) => ({
        key: cluster.label,
        value: cluster.label,
        text: `${cluster.label} (${cluster.count})`,
        cluster,
        edgeCount: cluster.edgeCount,
        typeName: cluster.typeName,
        isKMeansCluster: cluster.isKMeansCluster,
        ...(cluster.isKMeansCluster && {
          maxWidth: cluster.maxWidth,
          maxHeight: cluster.maxHeight,
          minWidth: cluster.minWidth,
          minHeight: cluster.minHeight,
        }),
      }))
      .sort((a, b) => {
        if (a.edgeCount !== b.edgeCount) {
          return a.edgeCount - b.edgeCount;
        }

        if (
          a.edgeCount === 4 &&
          b.edgeCount === 4 &&
          a.isKMeansCluster &&
          b.isKMeansCluster
        ) {
          const aArea = a.maxWidth * a.maxHeight;
          const bArea = b.maxWidth * b.maxHeight;
          const areaDiff = bArea - aArea;
          if (areaDiff !== 0) return areaDiff;

          const widthDiff = b.maxWidth - a.maxWidth;
          if (widthDiff !== 0) return widthDiff;

          const heightDiff = b.maxHeight - a.maxHeight;
          return heightDiff;
        }

        return b.cluster.count - a.cluster.count;
      });
  }, [allWindows]);

  const areaOptions = useMemo(() => {
    const allAreas = [];
    const windowAreaMap = new Map();

    allWindows.forEach((window) => {
      if (window.polygon) {
        const rawArea = calculateArea(window.polygon);
        allAreas.push(rawArea);
        windowAreaMap.set(window.id || window.polygon, rawArea);
      }
    });

    if (allAreas.length === 0) {
      return [];
    }

    const { clusters } = kMeansClustering(allAreas, 20);

    return clusters
      .map((cluster) => ({
        key: cluster?.centroid,
        value: cluster?.centroid,
        text: `${cluster?.centroid?.toFixed(1)} (${cluster?.count})`,
        cluster: cluster,
        range: `${cluster?.min?.toFixed(2)}-${cluster?.max?.toFixed(2)}`,
      }))
      .sort((a, b) => {
        return b.value - a.value;
      });
  }, [allWindows]);

  useEffect(() => {
    if (shapeSizeOptions.length > 0 && areaOptions.length > 0) {
      const dimensionClusters = shapeSizeOptions
        .filter((option) => option.isKMeansCluster)
        .map((option) => option.cluster);
      const areaClusters = areaOptions.map((option) => option.cluster);

      dispatch(
        setClusterData({
          areaClusters,
          dimensionClusters,
        })
      );
    }
  }, [dispatch, shapeSizeOptions, areaOptions]);

  const handleFilterChange = (filterType, value) => {
    onFiltersChange({
      ...filters,
      [filterType]: value,
    });
  };

  const clearFilter = (filterType) => {
    if (
      filterType === "floor" ||
      filterType === "shapeSize" ||
      filterType === "area"
    ) {
      handleFilterChange(filterType, []);
    } else {
      handleFilterChange(filterType, "");
    }
  };

  return (
    <div className={`window-filters main-screen`}>
      <div className={`filters-grid main-screen-grid`}>
        <div className="filter-group">
          <Form.Label className="filter-label">Floor</Form.Label>
          <Dropdown
            item
            simple
            text={
              filters.floor.length === floorOptions.length ||
              filters.floor.length === 0
                ? "All Floors"
                : filters.floor.length === 1
                ? floorOptions.find((f) => f.value === filters.floor[0])?.text
                : `${filters.floor.length} floors selected`
            }
            className="sensor-filter-dropdown"
          >
            <Dropdown.Menu
              className="kkk drop-down-menu"
              style={{ marginTop: -20 }}
            >
              <Dropdown.Item
                onClick={() => {
                  const isAllSelected =
                    filters.floor.length === floorOptions.length;
                  handleFilterChange(
                    "floor",
                    isAllSelected ? [] : floorOptions.map((f) => f.value)
                  );
                }}
              >
                <Checkbox
                  checked={filters.floor.length === floorOptions.length}
                  label="Select All"
                />
              </Dropdown.Item>
              {floorOptions.map((floor) => (
                <Dropdown.Item
                  key={floor.value}
                  onClick={() => {
                    const newValues = filters.floor.includes(floor.value)
                      ? filters.floor.filter((v) => v !== floor.value)
                      : [...filters.floor, floor.value];
                    handleFilterChange("floor", newValues);
                  }}
                >
                  <Checkbox
                    checked={filters.floor.includes(floor.value)}
                    label={floor.text}
                  />
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>

        <div className="filter-group">
          <Form.Label className="filter-label">Name</Form.Label>
          <InputGroup className="filter-input-group">
            <InputGroup.Text>
              <FaSearch />
            </InputGroup.Text>
            <Form.Control
              type="text"
              placeholder="Search window names..."
              value={filters.name}
              onChange={(e) => handleFilterChange("name", e.target.value)}
              className="filter-input"
            />
            <Button
              variant="outline-secondary"
              onClick={() => clearFilter("name")}
              className="clear-input-btn"
            >
              <FaTimes />
            </Button>
          </InputGroup>
        </div>

        <div className="filter-group">
          <Form.Label className="filter-label">Shape & Size</Form.Label>
          <Dropdown
            item
            simple
            text={
              filters.shapeSize.length === shapeSizeOptions.length ||
              filters.shapeSize.length === 0
                ? "All Sizes"
                : filters.shapeSize.length === 1
                ? shapeSizeOptions.find((s) => s.value === filters.shapeSize[0])
                    ?.text
                : `${filters.shapeSize.length} sizes selected`
            }
            className="sensor-filter-dropdown"
          >
            <Dropdown.Menu
              className="kkk drop-down-menu"
              style={{ marginTop: -20 }}
            >
              <Dropdown.Item
                onClick={() => {
                  const isAllSelected =
                    filters.shapeSize.length === shapeSizeOptions.length;
                  handleFilterChange(
                    "shapeSize",
                    isAllSelected ? [] : shapeSizeOptions.map((s) => s.value)
                  );
                }}
              >
                <Checkbox
                  checked={filters.shapeSize.length === shapeSizeOptions.length}
                  label="Select All"
                />
              </Dropdown.Item>
              {shapeSizeOptions.map((shape) => (
                <Dropdown.Item
                  key={shape.value}
                  onClick={() => {
                    const newValues = filters.shapeSize.includes(shape.value)
                      ? filters.shapeSize.filter((v) => v !== shape.value)
                      : [...filters.shapeSize, shape.value];
                    handleFilterChange("shapeSize", newValues);
                  }}
                >
                  <Checkbox
                    checked={filters.shapeSize.includes(shape.value)}
                    label={shape.text}
                  />
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
        <div className="filter-group">
          <Form.Label className="filter-label">Area</Form.Label>
          <Dropdown
            item
            simple
            text={
              filters?.area?.length === areaOptions?.length ||
              filters?.area?.length === 0
                ? "All Areas"
                : filters?.area?.length === 1
                ? areaOptions.find((s) => s.value === filters?.area[0])?.text
                : `${filters?.area?.length ?? 0} areas selected`
            }
            className="sensor-filter-dropdown"
          >
            <Dropdown.Menu
              className="kkk drop-down-menu"
              style={{ marginTop: -20 }}
            >
              <Dropdown.Item
                onClick={() => {
                  const isAllSelected =
                    filters?.area?.length === areaOptions?.length;
                  handleFilterChange(
                    "area",
                    isAllSelected ? [] : areaOptions.map((s) => s.value)
                  );
                }}
              >
                <Checkbox
                  checked={filters?.area?.length === areaOptions?.length}
                  label="Select All"
                />
              </Dropdown.Item>
              {areaOptions.map((area) => (
                <Dropdown.Item
                  key={area.value}
                  onClick={() => {
                    const newValues = filters?.area?.includes(area.value)
                      ? filters?.area?.filter((v) => v !== area.value)
                      : [...filters.area, area.value];
                    handleFilterChange("area", newValues);
                  }}
                >
                  <Checkbox
                    checked={filters?.area?.includes(area.value)}
                    label={area.text}
                  />
                </Dropdown.Item>
              ))}
            </Dropdown.Menu>
          </Dropdown>
        </div>
      </div>
    </div>
  );
};

export default WindowFilters;
